import {Timestamp} from '@mintdata-wsp/common/state/ui/visualDesigner/DesignVersionsState';
import {Flow} from '@mintdata-wsp/common/state/ui/visualDesigner/FlowState';
import {
    IVisualStreamId,
    VisualDataDirection,
    VisualStreamId,
    VisualStreamIds,
    VisualStreamKey
} from '@mintdata-wsp/common/state/ui/visualDesigner/visualData/VisualStreamIds';
import {IVisualDataStreamer, VisualDataStreamer} from '@mintdata-wsp/wsp/controllers/visualDesigner/visualData/VisualDataStreamer';
import {DateTimeUtil} from '@mintdata-wsp/wsp/util/DateTimeUtil';
import {IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';
import bind from 'bind-decorator';
import {action, observable} from 'mobx';

export class FrameEntry
{
    private constructor(public readonly timestamp:Timestamp,
                        public readonly frame:IFrame)
    {
    }

    public static withTimestamp(frame:IFrame):FrameEntry
    {
        const timestamp = DateTimeUtil.getNow();
        return new FrameEntry(timestamp, frame);
    }
}

export class VisualDataTimeline
{
    @observable
    private readonly _frameEntries:Array<FrameEntry> = [];

    constructor(public readonly streamId:VisualStreamId)
    {
    }

    @action.bound
    public clearTimeline():void
    {
        this._frameEntries.splice(0, this._frameEntries.length);
        // this._frameEntries = [];
    }

    @bind
    public getFrameEntries():FrameEntry[]
    {
        return this._frameEntries;
    }
}

export class TimelineManager
{
    @observable
    private _idToTimeline:Map<VisualStreamKey, VisualDataTimeline> = new Map();

    @action.bound
    public getOrCreateTimeline(streamId:VisualStreamId):VisualDataTimeline
    {
        let key = streamId.uniqueKey;
        let timeline = this._idToTimeline.get(key);

        if( !timeline )
        {
            timeline = new VisualDataTimeline(streamId);
            this._idToTimeline.set(key, timeline);
        }

        return timeline;
    }

    @action.bound
    public clearAllTimelines(flow?:Flow)
    {
        let timelines = [];

        if( flow )
        {
            const flowStreamKeys = flow.flowBlocks.map((flowBlock) => (
                VisualStreamId.generateUniqueKey(flowBlock.id, VisualDataDirection.Southbound)
            ));

            timelines = Array.from(this._idToTimeline.keys())
                .filter((key) => flowStreamKeys.indexOf(key) !== -1)
                .map((key) => this._idToTimeline.get(key));
        }
        else
        {
            timelines = Array.from(this._idToTimeline.values());
        }

        for( const timeline of timelines )
        {
            timeline.clearTimeline();
        }
    }
}

export class StreamerManager
{
    @observable
    private _idToStreamer:Map<VisualStreamKey, IVisualDataStreamer> = new Map();

    constructor(private _timelineManager:TimelineManager)
    {
    }

    @action.bound
    public getOrCreateStreamer(streamId:IVisualStreamId):IVisualDataStreamer
    {
        let key = streamId.uniqueKey;
        let streamer = this._idToStreamer.get(key);

        if( !streamer )
        {
            streamer = new VisualDataStreamer(streamId, this._timelineManager);
            this._idToStreamer.set(key, streamer);
        }

        return streamer;
    }

    public getStreamer(streamId:IVisualStreamId):IVisualDataStreamer
    {
        return this._idToStreamer.get(streamId.uniqueKey);
    }

    @action.bound
    public clearAllStreamers(flow?:Flow)
    {
        let streamers = [];

        if( flow )
        {
            let flowStreamKeys = flow.flowBlocks.map((flowBlock) =>
            {
                const streams = VisualStreamIds.forBlock(flowBlock);
                return [streams.input, streams.output].map(({uniqueKey}) => uniqueKey);
            });

            flowStreamKeys = [].concat(...flowStreamKeys);

            streamers = Array.from(this._idToStreamer.keys())
                .filter((key) => flowStreamKeys.indexOf(key as any) !== -1)
                .map((key) => this._idToStreamer.get(key));
        }
        else
        {
            streamers = Array.from(this._idToStreamer.values());
        }

        for( const streamer of streamers )
        {
            streamer.reset();
        }
    }
}

export class VisualDataManager
{
    private readonly _timelineManager:TimelineManager;
    private readonly _streamerManager:StreamerManager;

    constructor()
    {
        this._timelineManager = new TimelineManager();
        this._streamerManager = new StreamerManager(this._timelineManager);
    }

    public getOrCreateTimeline(streamId:VisualStreamId):VisualDataTimeline
    {
        return this._timelineManager.getOrCreateTimeline(streamId);
    }

    @bind
    public appendFrame(frame:IFrame, direction:VisualDataDirection):void
    {
        const streamId = VisualStreamId.fromFrame(frame, direction);

        if( streamId.isStreamSet )
        {
            console.log('Error: frames can be appended only to non-set streamIds');
            return;
        }

        const mgr = this._timelineManager;
        const singleStreamId = streamId as VisualStreamId;
        const frameEntry = FrameEntry.withTimestamp(frame);
        const timeline = mgr.getOrCreateTimeline(singleStreamId);

        const frameEntries = timeline.getFrameEntries();

        frameEntries.push(frameEntry);
    }

    @bind
    public getOrCreateStreamer(streamId:IVisualStreamId):IVisualDataStreamer
    {
        return this._streamerManager.getOrCreateStreamer(streamId);
    }

    @bind
    public getStreamer(streamId:IVisualStreamId):IVisualDataStreamer
    {
        return this._streamerManager.getStreamer(streamId);
    }

    public clearCurrentSession(flow?:Flow):void
    {
        // Note: ordering of these 2 calls is very important, because timelines will
        // first set Mobx observable frameEntries to [], and then streamers will
        // re-subscribe to changes on these new objects from the timeline manager.
        // However, streamers will keep their gridModel instance intact (by design).
        this._timelineManager.clearAllTimelines(flow);
        this._streamerManager.clearAllStreamers(flow);
    }
}
